import { createRouter, createWebHistory } from 'vue-router'

// Route-fork PR-R4: Client Portal route group. Imported as a fully-formed
// array of route objects (with absolute paths + per-route guards already
// baked in) so we can spread it into the top-level routes table without
// going through withPrefix(). The other route groups (`distributor.js`,
// `superadmin.js`, `storefront.js`) are still structural seams populated
// by PR-R2 page moves; PR-R4 is the first group to be live-wired.
import portalRoutes from '@/router/groups/portal'
// SA-only `/admin/*` surface. Same shape as portalRoutes: the group carries
// absolute paths + per-route guards + meta.layout, so it is spread directly
// into the routes table (NOT via withPrefix). This wiring reconciles the
// former inline /admin route arrays (which lacked meta.layout and did not
// register /admin/operations + /admin/risk) with the single-source group.
import superadminRoutes from '@/router/groups/superadmin'
// Shopper-facing `/shop`, `/store/:slug/*` surface. Group exports absolute
// paths and per-route meta.layout = 'storefront', so it is spread directly
// (NOT via withPrefix) -- same pattern as portalRoutes/superadminRoutes.
//
// Before fix/wire-storefront-routes (issue #1) this group was exported but
// NEVER imported. The Phase 2-A gallery (`/store/:slug/products`, PR #1474)
// and Phase 2-B detail (`/store/:slug/products/:id`, PR #1491) 404'd
// because they had no inline equivalent in publicRoutes. The other
// storefront paths (`/store/:id`, `/store/:slug/cart`, ...) happened to
// also be defined inline so they worked, but as duplicate sources of
// truth that had already drifted (the flat gallery + detail pages were
// absent inline). Wiring the group in here and removing the inline
// duplicates from publicRoutes (below) makes storefront.js the single
// source again. (Those flat gallery/detail surfaces were later retired by
// P8 and their components deleted as dead code -- the legacy paths are now
// redirects in router/redirects.js.)
import storefrontRoutes from '@/router/groups/storefront'
// Distributor Clients list surface -- a table (Name / Email / Phone /
// Stores / Status / Actions) with a per-row deactivate (soft-delete)
// action, plus the per-Client workspace detail. The legacy F5 seed
// ``ClientList.vue`` was removed (2026-07-13) once this component became
// the sole /clients surface.
import ClientsListPage from '@/pages/distributor/clients/ClientsListPage.vue'
import ClientDetailPage from '@/pages/distributor/clients/ClientDetailPage.vue'
import DecorationLibraryList from '@/pages/distributor/decoration-library/DecorationLibraryList'
import DecorationAddWizard from '@/pages/distributor/decoration-library/DecorationAddWizard'
// P5 PR-B (Boulders #163): the legacy DecorationDetailPage was the
// component target for ``decoration_library_detail`` in P3.1; it is
// replaced by the legacy-redirect resolver below + the new
// artwork-rooted detail page. The component file remains on disk
// (its sub-components ArchivedInUseWarning, PerClientApprovalMatrix,
// etc. are still referenced from other surfaces) but it is no longer
// mounted as a route component.
// P5 PR-B (Boulders #163): method-tabbed per-Artwork detail page +
// legacy redirect resolver. The legacy ``/decoration-library/:id``
// route is repointed to the resolver, which mints the new named route
// ``library_artwork_detail`` mounted at
// ``/decoration-library/artwork/:artwork_id``.
import DecorationLibraryArtworkDetail from '@/pages/distributor/decoration-library/detail/DecorationLibraryArtworkDetail.vue'
import DecorationLibraryLegacyRedirect from '@/pages/distributor/decoration-library/detail/DecorationLibraryLegacyRedirect.vue'
import ColorwayEditor from '@/pages/distributor/decoration-library/detail/ColorwayEditor.vue'
import DesignDetailPage from '@/pages/distributor/decoration-library/detail/family/DesignDetailPage.vue'
import DecorationSetsListPage from '@/pages/distributor/decoration-sets/DecorationSetsListPage.vue'
import DecorationSetDetailPage from '@/pages/distributor/decoration-sets/DecorationSetDetailPage.vue'
import ApprovalWorkspacePage from '@/pages/distributor/approvals/ApprovalWorkspacePage.vue'

import CampaignAddEdit from '@/pages/distributor/store-editor/StoreAddEdit'
import StoreDesignDetail from '@/pages/distributor/store-editor/StoreDesignDetail.vue'
import { OLD_TAB_TO_SECTION } from '@/pages/distributor/store-editor/sectionRoutes'
import CampaignList from '@/pages/distributor/store-editor/StoreList'
import DistributorProfile from '@/pages/distributor/account/DistributorProfile'
// Suppliers (supplier accounts) as its own Account page. Was
// DistributorProfile.vue's second tab until 2026-09-24, then "Integrations";
// renamed Suppliers and stripped of Stripe Connect (Payments owns it) in IA
// unit U1, 2026-10-19. The component keeps its file name.
import DistributorIntegrations from '@/pages/distributor/account/DistributorIntegrations'
// Personal Profile page (card https://trello.com/c/BxskXBad). ONE page
// component mounted by all four apps' profile routes; each route's
// meta.layout supplies that app's chrome.
import PersonalProfilePage from '@/pages/PersonalProfilePage.vue'
import ConfirmEmailChangePage from '@/pages/ConfirmEmailChangePage.vue'
import TeamPermissionsPage from '@/pages/distributor/account/TeamPermissionsPage.vue'
import InviteTeammatePage from '@/pages/distributor/account/InviteTeammatePage.vue'
import PaymentsPage from '@/pages/distributor/account/PaymentsPage.vue'
// HIDDEN 2026-09-03 -- account-level notification preferences.
// The page renders the 19-event roster in api/src/notifications/
// event_roster.py, which has ZERO overlap with the live senders in
// plugins/mail.py: it came from PR #1326, a UI feature that needed rows
// to render. Toggling all 19 events changed nothing about what mail
// anyone received. The STORE-level matrix (DEFAULT_NOTIFICATION_
// PREFERENCES + notifications/gating.py) is the one whose toggles gate
// real sends, and Jake's call was to extend that and drop this -- two
// parallel preference systems is how #2815 happened.
// The component, its endpoint, the model and the migration all remain.
// To restore: uncomment this import and the route below.
// import NotificationPreferences from '@/pages/distributor/account/NotificationPreferences.vue'
import CompliancePage from '@/pages/distributor/account/compliance/CompliancePage.vue'
import AgreementPage from '@/pages/legal/AgreementPage.vue'
import DistributorComingSoon from '@/pages/distributor/DistributorComingSoon.vue'
// Distributor Account Defaults: a single /account/defaults page that
// hosts the Pricing + Branding settings as two stacked cards (IA collapse
// 2026-06-24). AccountDefaults imports the two card components itself;
// the former /defaults/pricing and /defaults/branding sub-routes were
// removed, so the router no longer imports those components directly.
import AccountDefaults from '@/pages/distributor/account/defaults/AccountDefaults.vue'
import ProductAddEdit from '@/components/Product/ProductAddEdit'
import ProductEditor from '@/pages/distributor/store-editor/StoreProducts/Editor/ProductEditor.vue'
import ProductList from '@/components/Product/ProductList'
import ProductView from '@/components/Product/ProductView'
import Report from '@/pages/distributor/reporting'
import PerformanceDashboard from '@/pages/distributor/reporting/PerformanceDashboard.vue'
import DistributorOrdersList from '@/pages/distributor/reporting/DistributorOrdersList.vue'
import DistributorNewOrderPage from '@/pages/distributor/reporting/DistributorNewOrderPage.vue'
import DistributorOrderDetail from '@/pages/distributor/reporting/DistributorOrderDetail.vue'
import DistributorPayoutsPage from '@/pages/distributor/reporting/DistributorPayoutsPage.vue'
import DistributorReportsPage from '@/pages/distributor/reporting/DistributorReportsPage.vue'
// Storefront components are imported by router/groups/storefront.js (the
// single source for shopper-facing routes since fix/wire-storefront-routes,
// issue #1). No direct imports needed here.
import TheHomePage from '@/components/TheHomePage'
import TheLoginPage from '@/components/TheLoginPage'
import TheResetPasswordPage from '@/components/TheResetPasswordPage'
// Phase F5 / ADR 0050: Client Store Owner invitation magic-link page.
import AcceptInvitation from '@/pages/auth/AcceptInvitation.vue'
import TheNotFoundPage from '@/components/TheNotFoundPage'
// The VendorAddEdit + VendorList imports left here 2026-06-08 when the
// vendor routes moved to `groups/superadmin.js` (eba0afb52). Both
// components, and the routes that mounted them, were deleted outright
// 2026-08-28 as legacy OrderWrite surface.
// The legacy `/tutorials/*` + `/guides/*` help imports were REMOVED
// 2026-08-18 along with their routes and components
// (`docs/user/HELP-SYSTEM-DESIGN.md` decision 5): OrderWrite-era
// "campaign"/"pop-up" content and video cards, replaced by the
// markdown-backed `/help/*` surfaces below. Deleted outright rather than
// redirected -- ThreadFlow has never launched to production, so there are
// no inbound links to preserve.
import OwnerHelpPage from '@/pages/help/OwnerHelpPage.vue'
import SignUp from '@/components/SignUp/SignUp'
import AddCredentials from '@/components/SignUp/AddCredentials'
import DistributorLanding from '@/pages/distributor/DistributorLanding.vue'
// Note: DevConsole, SubscriptionList, SubscriptionAddEdit, AccountList,
// AccountAddEdit, DistributorList, DistributorAddEdit, SuperAdminDashboard,
// and the PR #1331 Phase 3 mock-data pages (RiskWatch, PlansPricing,
// Money) are now imported by groups/superadmin.js -- removed
// here when the inline /admin route arrays were consolidated into the SA
// route group (Workstream-C 2026-05-29).
import PricingPage from '@/components/Pricing/PricingPage'
import store from '@/store'
import { authMe } from '@/services/accountService'
// PR-7 (frontend-rearchitecture): capability-based router guard.
//
// `requireCapability(key)` is the route-guard factory. It reads capabilities
// from the Pinia `useAccountStore` (PR-6); the keys are defined in the
// backend registry at `api/src/models/capabilities.py` and surfaced in the
// login response (PR-4).
//
// ACL-PR-13f: the legacy `requireRole(allowedRoleNames, redirectTo)` factory
// has been fully removed. Every route in this file now gates on a capability
// key. See `docs/planning/acl-pr-13-blockers-2026-05-12.md` for the
// chain-wide decision context.
//
// The factory is defined in a sibling file (`./requireCapability`) so its
// unit tests can import it without dragging in the .vue component imports
// that this index.js carries.
import { requireCapability } from './requireCapability'
import { requireAddNewOrderEnabled } from './requireAddNewOrderEnabled'
// PR-12 (issue #901): page-reload rehydration. `requireToken` is built
// from the `createRequireToken({store, authMe})` factory so it can be
// unit-tested in isolation (same pattern as `requireCapability`).
import { createRequireToken } from './requireToken'
import { runDeeplinkImpersonation } from './deeplinkImpersonationRuntime'
// Store-editor preview: keeps `?preview=1` attached across in-iframe
// navigation so the preview iframe can never escape into a normal shopper
// route (whose token swap would clobber the editor's session).
import { installPreviewQueryPersistence } from './storefrontPreview'
import { installHelpFramedQueryPersistence } from './helpFramed'
import { installNotificationDismissOnRouteChange } from './dismissNotificationOnRouteChange'
import { installResumeFragment } from './storefrontResumeCode'
import {
    installStorefrontBrowserChrome,
    installStorefrontChromeWatch,
} from './storefrontBrowserChrome'
// Store-wide password wall. A password-protected store must not be reachable
// on ANY of its pages without the shared password. This is a GUARD rather than
// a component because the shopper session routes (cart, orders, profile,
// addresses, payment-methods) are SIBLING route records of the session gate,
// not children -- so a component gate mounted on `/shop/:slug` never covered
// them. See router/storefrontPasswordGate.js.
import {
    createStorefrontPasswordGate,
    createAxiosLookup,
} from './storefrontPasswordGate'
import axiosInstance from '@/axios'
// PR-R5: legacy flat storefront paths (/cart, /orders, /account/...) move
// to nested /store/:slug/* paths. The REDIRECTS map carries the backward-
// compat entries (some string, some function destinations for slug-aware
// forwarding). See router/redirects.js.
import REDIRECTS from './redirects'
export { requireCapability }

// helpers
// Non-mutating: returns a NEW route object with the prefixed path. Mutating
// the shared route objects in place (the previous behaviour) meant a second
// evaluation of this module -- as happens under vitest's transform pipeline --
// re-prefixed already-prefixed paths and produced duplicate / malformed route
// records (e.g. a nameless `/admin` shadowing the real `sa_dashboard` one).
function withPrefix(prefix, routes, sharedMeta = null) {
    // Non-mutating: returns NEW route objects with the prefixed path. If
    // sharedMeta is provided, it's shallow-merged onto each route's meta
    // (the route's own meta wins on conflict). Used to inject
    // meta.layout for whole route groups -- e.g. all /distributor/*
    // routes get meta.layout = 'distributor' so App.vue's currentLayout
    // computed picks DistributorLayout (sidebar + topbar + ImpersonationBanner).
    return routes.map(route => {
        const merged = { ...route, path: prefix + route.path };
        if (sharedMeta) {
            merged.meta = { ...sharedMeta, ...(route.meta || {}) };
        }
        return merged;
    });
}

// middleware
// PR-12 (issue #901): build the page-reload-aware requireToken guard. The
// implementation lives in `./requireToken` so it can be unit-tested without
// dragging in this file's .vue imports.
// `deeplink`: deep-link auto-impersonation on the first navigation of a page
// load (router/deeplinkImpersonation.js).
const requireToken = createRequireToken({
    store,
    authMe,
    deeplink: runDeeplinkImpersonation
})

// routes
// SA-only /admin/* surface is defined in router/groups/superadmin.js and
// spread directly into the routes table below as `...superadminRoutes`.
// That group is the single source for SA route definitions -- includes the
// bare-/admin SuperAdminDashboard landing (sa_dashboard), cross-tenant
// accounts, cross-tenant distributors (with PR #1331 mock /full preview),
// DevConsole at /admin/operations (its sole mount as of 2026-08-28),
// subscription admin, and PR #1331's Phase 3 mock pages (risk-watch,
// plans-pricing, money). The former inline arrays here (saDashboardRoute,
// accountRoutes, saDistributorRoutes, adminAreaOtherPages) were removed
// when the consolidation landed (Workstream-C 2026-05-29).

// Distributor-surface PERSONAL profile route.
//
// Card: "Personal Profile page" -- https://trello.com/c/BxskXBad
//
// NAMING COLLISION RESOLVED (2026-08-11): `/distributor/profile` used to
// mount DistributorProfile.vue -- the COMPANY profile (business info +
// integrations), which is a tenant-level surface, not a personal one. It
// now mounts the personal Profile page, and the company page moved to
// the unambiguous `/distributor/account/company` ("Company Profile")
// where it joins the other Owner-level business settings under
// `/account/*`. Both former company paths redirect there (see
// `distributorAccountRoutes` below).
//
// NO capability guard: this is the signed-in user's OWN profile. Every
// role that can log in has one, including Shoppers, so gating it on a
// distributor capability would lock users out of their own account
// settings. The backend is self-scoped to the bearer token, so there is
// nothing here a guard could protect.
const distributorProfileRoutes = [
    {
        path: '/profile',
        name: 'distributor_personal_profile',
        component: PersonalProfilePage
    }
]

// Distributor App Account group -- Owner-only nested routes.
//
// Polish item 2026-06-04: the sidebar links these routes (DistributorLayout
// "/distributor/account/*") and the page components exist (PRs #1305, #1315,
// #1326), but the previous routing scaffold was deleted with the legacy
// distributor.js group file (PR #1329) without being migrated into this
// file. Re-add the team + invite routes here; the rest of the Account
// group (defaults / compliance / notifications / payments) was rescued
// in adjacent polish work or remains as follow-up.
//
// The /team/invite sub-route formalises the previously-modal invite
// flow as a real URL with a beforeRouteLeave dirty-form guard, matching
// the campaign-editor pattern (StoreAddEdit, PR #1329).
const distributorAccountRoutes = [
    {
        // Bare /distributor/account had no route after the legacy
        // distributor.js group file was removed in PR #1329. Browser visits
        // 404'd. Redirect to the company profile (the closest thing to a
        // landing among current account sub-routes). Repointed from
        // /account/profile to /account/company with the 2026-08-11
        // consolidation. When the audit-driven distributor-account landing
        // page ships, this redirect gets repointed there.
        path: '/account',
        redirect: '/distributor/account/company'
    },
    {
        path: '/account/team',
        name: 'distributor_account_team',
        component: TeamPermissionsPage,
        beforeEnter: requireCapability('distributor.account.read', '/distributor')
    },
    {
        path: '/account/team/invite',
        name: 'distributor_account_team_invite',
        component: InviteTeammatePage,
        beforeEnter: requireCapability('distributor.account.read', '/distributor')
    },
    // Ported from the deleted router/groups/distributor.js scaffold
    // (PR #1329 correctly removed the dead file; PR #1333 brought back
    // team + team/invite; this PR brings back the remaining Account
    // group routes so DistributorLayout's sidebar links stop 404'ing).
    // Profile / Payments / Notifications mount real components shipped
    // in PRs #1305 / #1315 / #1326. Defaults + Compliance index pages
    // render DistributorComingSoon as a deliberate frame -- the real
    // pages depend on backend work (distributor.pricing.defaults +
    // distributor.compliance.manage surfaces) that hasn't shipped yet.
    // The compliance/tax-nexus child route mounts a real editor (PR
    // #1305) and is gated on its own narrower capability so a Builder
    // who shouldn't write compliance settings is blocked even when
    // the sidebar link to /account/compliance is loosened later.
    {
        // COMPANY profile -- the distributor's business info + supplier
        // integrations. Card https://trello.com/c/BxskXBad consolidated the
        // two paths that used to mount this page (`/profile` and
        // `/account/profile`, both DistributorProfile.vue) onto this ONE
        // unambiguous route, labeled "Company Profile" in the sidebar.
        //
        // Why here: it joins the existing `/account/*` Owner group (team,
        // payments, compliance, notifications) where business settings
        // already live, and "company" cannot be confused with the personal
        // page -- which is exactly what frees plain "Profile" for the
        // personal profile at `/distributor/profile`.
        //
        // The `distributor.account.read` guard is carried over unchanged
        // from the route this replaces: this page shows tenant business
        // data and is NOT personal, so it stays gated.
        path: '/account/company',
        name: 'distributor_account_company',
        component: DistributorProfile,
        beforeEnter: requireCapability('distributor.account.read', '/distributor')
    },
    {
        // SUPPLIERS (was INTEGRATIONS) -- supplier accounts only. Stripe
        // Connect lives on Payments alone (IA unit U1, D3). Was the second
        // tab on Company Profile until 2026-09-24, when Jake moved it out to
        // its own Account page "just below the Company Profile page".
        //
        // Placed immediately after /account/company here so the route table
        // reads in the same order the sidebar renders. That is a readability
        // choice, not a functional one -- the nav ORDER is asserted against
        // the rendered sidebar in distributor-integrations-page.spec.js,
        // because route-declaration order does not drive it.
        //
        // The guard is IDENTICAL to Company Profile's, deliberately. This is
        // the gate Integrations already sat behind: the tab was reachable
        // only by loading /account/company, which requires
        // distributor.account.read. Carrying the same capability across means
        // the move grants nobody access they lacked and revokes none they had.
        path: '/account/suppliers',
        name: 'distributor_account_suppliers',
        component: DistributorIntegrations,
        beforeEnter: requireCapability('distributor.account.read', '/distributor')
    },
    {
        // PERMANENT redirect from the pre-rename path. Welcome emails and
        // the launch-gate copy already sent to real distributors link to
        // /distributor/account/integrations, so this must never 404. A
        // string redirect carries the query over.
        path: '/account/integrations',
        redirect: '/distributor/account/suppliers'
    },
    {
        // Backward compatibility for the SHIPPED path. Sidebar links,
        // bookmarks, and anything a user currently has open point here;
        // this must keep working, so the old path redirects rather than
        // 404ing. Kept as a redirect (not an alias) so the canonical URL
        // is what ends up in the address bar and in any new bookmark.
        path: '/account/profile',
        redirect: '/distributor/account/company'
    },
    {
        path: '/account/payments',
        name: 'distributor_account_payments',
        component: PaymentsPage,
        beforeEnter: requireCapability('distributor.account.read', '/distributor')
    },
    {
        // 2026-06-24 (IA collapse): the Defaults surface is now a SINGLE
        // page. AccountDefaults hosts the Pricing + Branding settings as
        // two stacked cards; the former /defaults/pricing and
        // /defaults/branding sub-routes are gone. The page stays gated on
        // the read-tier distributor.account.read so Builders can VIEW it;
        // per-card WRITE authorization moved into the page itself
        // (usePermissions().can('distributor.pricing.defaults') /
        // can('distributor.branding.defaults') -- peer Owner-only caps,
        // NOT an alias pair, per the bar-raiser P0 on PR-F). The backend
        // PATCH endpoints enforce those caps server-side as defense in
        // depth.
        path: '/account/defaults',
        name: 'distributor_account_defaults',
        component: AccountDefaults,
        beforeEnter: requireCapability('distributor.account.read', '/distributor')
    },
    {
        // 2026-06-24 (card QV2U4KhF): real Compliance surface -- tax nexus
        // and the Master Services Agreement status (its confirm lives on
        // /legal/agreement). Gate stays at distributor.account.read; every
        // write endpoint enforces Owner-only itself.
        path: '/account/compliance',
        name: 'distributor_account_compliance',
        component: CompliancePage,
        beforeEnter: requireCapability('distributor.account.read', '/distributor')
    },
    {
        // Nexus now lives inline on the Compliance page as card 1; the
        // standalone editor sub-route is redundant. Redirect preserves any
        // existing bookmark.
        path: '/account/compliance/tax-nexus',
        redirect: { name: 'distributor_account_compliance' }
    },
    // HIDDEN 2026-09-03 -- see the NotificationPreferences import comment
    // at the top of this file. Superseded by the STORE-level matrix in
    // the store editor's Email Notifications card.
    // {
    //     path: '/account/notifications',
    //     name: 'distributor_account_notifications',
    //     component: NotificationPreferences,
    //     beforeEnter: requireCapability('distributor.account.read', '/distributor')
    // }
]

// Nested store-editor surface (Owner + Builder). Replaces the legacy
// flat `/distributor/campaigns/:id` hash-navigation editor with real
// nested routes under `/distributor/stores/:id/edit/:section?`. The
// editor component (StoreAddEdit, imported as CampaignAddEdit) reads the
// active section from `$route.params.section`; the bare `/edit` URL is
// the Review and Launch landing. Gated on `distributor.stores.edit`
// (Owner + Builder), redirecting unprivileged users to `/distributor`.
const storeRoutes = [
    {
        path: '/stores',
        name: 'distributor_stores',
        component: CampaignList,
        beforeEnter: requireCapability('distributor.stores.edit', '/distributor')
    },
    {
        // Canonical add-store URL. (The legacy /distributor/campaigns/add
        // path was removed 2026-10-21 and now falls through to NotFound.)
        // Mounts CampaignAddEdit in new-store mode (no :id param).
        // Gated on distributor.stores.edit -- the same cap that
        // gates the editor proper. (Pre-port, the legacy URL was
        // gated on campaign.create; the new gate matches the rest
        // of the locked-IA Owner+Builder distributor.stores.* family.)
        path: '/stores/add',
        name: 'store_add',
        component: CampaignAddEdit,
        beforeEnter: requireCapability('distributor.stores.edit', '/distributor')
    },
    {
        // PR-G (2026-06-22) Checkout IA refactor: the prior section ids
        // ``checkout-fields`` and ``pricing-checkout`` were renamed to
        // ``checkout`` and ``pricing``. These two redirects keep older
        // URLs (bookmarks, deep links, links in saved messages) resolving
        // onto the new section ids instead of falling through to the
        // catch-all and landing on the editor home.
        //
        // Function-redirect form matches the pattern used by the
        // ``/admin/campaigns/:id`` redirect below. Vue Router 4's object-redirect
        // form does NOT auto-forward the :id path param when the target
        // is a named route, so navigating to /edit/checkout-fields would
        // throw "Missing required param 'id'" at runtime. The function
        // form receives the resolved source route and can rebuild the
        // target path explicitly.
        path: '/stores/:id/edit/checkout-fields',
        redirect: (to) => `/distributor/stores/${to.params.id}/edit/checkout`,
    },
    {
        path: '/stores/:id/edit/pricing-checkout',
        redirect: (to) => `/distributor/stores/${to.params.id}/edit/pricing`,
    },
    {
        // ADR 0070 -- the SECOND mount of the design detail page.
        //
        // "One design detail page, dual-mounted. The same page renders
        // from the Library and from inside the store editor, with the
        // breadcrumb and shell differing and the content identical."
        // The Library mount is `library_artwork_design_detail`; this is
        // the store one, and both resolve to the same DesignDetailPage
        // component (StoreDesignDetail is a context resolver, not a
        // second page -- see its header).
        //
        // ORDERING IS LOAD-BEARING. This MUST stay above
        // `/stores/:id/edit/:section?` below: that catch-all matches any
        // single segment after /edit, so registered the other way round
        // this path resolves to `store_edit` with section
        // "decorations" and the design silently never renders. The
        // symptom is not a 404 -- it is the store editor looking
        // completely normal on the wrong page. `store-design-detail-route.spec.js`
        // pins the resolution in both directions so a future reorder
        // fails there rather than in a browser.
        //
        // Gated on distributor.stores.edit, matching the editor it is
        // nested inside, so this is not the one ungated hole into a
        // design.
        path: '/stores/:id/edit/decorations/artwork/:artwork_id/design',
        meta: { deeplinkOwner: { type: 'store', param: 'id' } },
        name: 'store_decoration_design_detail',
        component: StoreDesignDetail,
        props: true,
        beforeEnter: requireCapability('distributor.stores.edit', '/distributor')
    },
    {
        path: '/stores/:id/edit/:section?',
        meta: { deeplinkOwner: { type: 'store', param: 'id' } },
        name: 'store_edit',
        component: CampaignAddEdit,
        beforeEnter: requireCapability('distributor.stores.edit', '/distributor')
    }
]

const orderRoutes = [
    // IA unit U1 (D3): Stripe Connect lives on Payments only. The legacy
    // CreditCardPayment page (in no menu) is no longer routed; its URL
    // redirects to Payments so old bookmarks and emails still land. The
    // component file was deleted by unit U2. A string redirect
    // carries the query (Stripe return params) over.
    {
        path: '/ccpayments',
        redirect: '/distributor/account/payments'
    }
]
// `/distributor/setup` is now a REDIRECT to the landing dashboard.
//
// It used to mount `OnboardingScreen` -- the legacy OrderWrite "3 Easy Steps"
// page, with a mascot illustration, rust-red (#fc4a30 / #fc485e) numerals off
// the ThreadFlow palette, and "Trust the Process" / "Need Help?" sections
// pointing at surfaces that no longer exist. The readiness checklist on the
// new landing covers what that page was for, from real backend state rather
// than three hardcoded cards, so the component is deleted.
//
// The path stays alive rather than 404ing because live callers send users
// here: `AddCredentials.vue` redirects freshly-invited owners to it after
// they set a password, `/admin/setup` redirects here (legacyAdminRedirects),
// and it may be bookmarked. A redirect record carries no `beforeEnter` --
// the target's own `distributor.reporting.read` guard applies on arrival, so
// a team member following an old invite link ends up on `/distributor/stores`
// exactly as they would from a fresh login.
const onboardingRoutes = [
    {
        path: '/setup',
        redirect: '/distributor'
    }
]

// Decoration Library routes (Owner + Builder; cap-gated). Phase 1 read-only
// list landed in PR #1312 but was not wired into the router; this group
// adds the missing /distributor/decoration-library mount alongside the
// Phase 1-late add wizard + edit. The list reads use the .read cap; the
// wizard + edit + archive write paths use .write.
const decorationLibraryRoutes = [
    {
        path: '/decoration-library',
        name: 'decoration_library_list',
        component: DecorationLibraryList,
        beforeEnter: requireCapability(
            'distributor.decoration_library.read',
            '/distributor'
        )
    },
    {
        path: '/decoration-library/new',
        name: 'decoration_library_add',
        component: DecorationAddWizard,
        beforeEnter: requireCapability(
            'distributor.decoration_library.write',
            '/distributor'
        )
    },
    {
        path: '/decoration-library/:id/edit',
        name: 'decoration_library_edit',
        component: DecorationAddWizard,
        props: true,
        beforeEnter: requireCapability(
            'distributor.decoration_library.write',
            '/distributor'
        )
    },
    {
        // P5 PR-B (Boulders #163, https://trello.com/c/S5kN5poU):
        // method-tabbed per-Artwork detail page. URL = ``/library/:artwork_id``
        // -- artwork-rooted per the locked P5 UI design decisions
        // (2026-06-23). Mounted under ``/decoration-library/artwork/...``
        // to share the existing decoration-library mount prefix.
        //
        // The PR-A list page (DecorationLibraryList) navigates here via
        // the named route ``library_artwork_detail``.
        //
        // Order matters: this static-prefix dynamic route MUST come
        // BEFORE the legacy ``/decoration-library/:id`` redirect below
        // so ``/decoration-library/artwork/123`` matches this route
        // rather than treating ``artwork`` as the ``:id`` value.
        path: '/decoration-library/artwork/:artwork_id',
        meta: { deeplinkOwner: { type: 'artwork', param: 'artwork_id' } },
        name: 'library_artwork_detail',
        component: DecorationLibraryArtworkDetail,
        props: true,
        beforeEnter: requireCapability(
            'distributor.decoration_library.read',
            '/distributor'
        )
    },
    {
        // Design detail on the FAMILY model -- sizes grouped under a
        // design, approval at size grain (card
        // https://trello.com/c/2hcgDM7J, handoff
        // planning/design-handoffs/handoff_2026-08-23/, ADR 0067).
        //
        // Its own route rather than a replacement for
        // ``library_artwork_detail``: that page (ADR 0063) still owns
        // the preview rail, the file record and the cascade-delete
        // plumbing, and PerClientApprovalMatrix.vue stays live until
        // its replacement ships. Two routes over one artwork is the
        // honest interim -- swapping the mount now would leave a blank
        // where those surfaces are.
        //
        // Read capability: this surface only READS the family payload.
        //
        // Placed with the other static-prefix routes and BEFORE the
        // legacy ``/decoration-library/:id`` redirect, for the same
        // reason called out above -- otherwise ``artwork`` would be
        // swallowed as an ``:id``.
        path: '/decoration-library/artwork/:artwork_id/design',
        meta: { deeplinkOwner: { type: 'artwork', param: 'artwork_id' } },
        name: 'library_artwork_design_detail',
        component: DesignDetailPage,
        props: true,
        beforeEnter: requireCapability(
            'distributor.decoration_library.read',
            '/distributor'
        )
    },
    {
        // Embroidery colorway editor (card https://trello.com/c/2hcgDM7J,
        // MUST-SHIP). Map an .emb's stops to orderable Madeira threads.
        //
        // Mounted on its OWN route rather than as a section inside
        // DecorationDetailPage because a parallel session holds
        // decoration-library/detail territory; a standalone route needs zero
        // edits to their files. Fold it into the detail page later if that
        // reads better once both streams land.
        //
        // Placed with the other static-prefix routes and BEFORE the legacy
        // ``/decoration-library/:id`` redirect for the same reason called out
        // above: otherwise ``colorways`` would be swallowed as an ``:id``.
        //
        // Write capability, not read: the editor exists to CHOOSE threads.
        path: '/decoration-library/artwork/:artwork_id/colorways',
        meta: { deeplinkOwner: { type: 'artwork', param: 'artwork_id' } },
        name: 'library_artwork_colorways',
        component: ColorwayEditor,
        props: true,
        beforeEnter: requireCapability(
            'distributor.decoration_library.write',
            '/distributor'
        )
    },
    {
        // P5 PR-B legacy URL redirect resolver. The legacy
        // ``decoration_library_detail`` route name is preserved (PR-A
        // and other call sites reference it); the component swaps to
        // ``DecorationLibraryLegacyRedirect`` which re-routes to the
        // new ``library_artwork_detail`` named route.
        //
        // The ``:id`` param is already an artwork id under the legacy
        // detail page's Pinia store (it fetches ``/api/artworks/<id>/``),
        // so the redirect is mechanical -- swap the param name and
        // route name.
        //
        // NOTE: order matters -- this dynamic route MUST sit AFTER the
        // static ``/decoration-library/new`` and
        // ``/decoration-library/artwork/...`` routes above so the
        // static prefixes match first.
        path: '/decoration-library/:id',
        meta: { deeplinkOwner: { type: 'artwork', param: 'id' } },
        name: 'decoration_library_detail',
        component: DecorationLibraryLegacyRedirect,
        props: true,
        beforeEnter: requireCapability(
            'distributor.decoration_library.read',
            '/distributor'
        )
    },
    {
        // Phase 4.8 Decoration Sets manager (card https://trello.com/c/hYlwyKYT,
        // pebble Phase 4.8). Reusable placement bundles -- save a configured
        // set once, apply to many products. Backend is the Phase 4.1 CRUD
        // surface at /api/decoration_sets/ (PR #1561). The list page reads
        // distributor.decoration_library.read; the create/edit modals
        // (P4.10 follow-up) carry .write checks at the component layer.
        path: '/decorations/sets',
        name: 'decoration_sets_list',
        component: DecorationSetsListPage,
        beforeEnter: requireCapability(
            'distributor.decoration_library.read',
            '/distributor'
        )
    },
    {
        // Phase 4.8 set detail. ``props: true`` so the component
        // receives :id as a prop (matches the decoration_library_detail
        // mount pattern above).
        path: '/decorations/sets/:id',
        meta: { deeplinkOwner: { type: 'decoration_set', param: 'id' } },
        name: 'decoration_sets_detail',
        component: DecorationSetDetailPage,
        props: true,
        beforeEnter: requireCapability(
            'distributor.decoration_library.read',
            '/distributor'
        )
    },
    {
        // Phase 2.1 Approval Workspace (card https://trello.com/c/hYlwyKYT).
        // List-only mount.
        path: '/decorations/approvals',
        name: 'approval_workspace',
        component: ApprovalWorkspacePage,
        beforeEnter: requireCapability(
            'distributor.decoration_library.read',
            '/distributor'
        )
    },
    {
        // Phase 2.1 Approval Workspace deep-link mount (P2-Q3): the same
        // shell + an opened detail panel for the given record.
        path: '/decorations/approvals/:approvalId',
        meta: { deeplinkOwner: { type: 'approval', param: 'approvalId' } },
        name: 'approval_workspace_detail',
        component: ApprovalWorkspacePage,
        props: true,
        beforeEnter: requireCapability(
            'distributor.decoration_library.read',
            '/distributor'
        )
    }
]

// Clients tab routes (issue #8 -- distributor Clients tab under Work).
// Read-gated for Owner + Builder via ``client.read``; write surfaces
// (inline edits + delete + store associations) check ``client.update``
// at the component layer + at the API.
const clientsRoutes = [
    {
        path: '/clients',
        name: 'client_list',
        // ClientsListPage renders the distributor Clients TABLE (with a
        // per-row deactivate soft-delete). It replaced the F5 seed
        // ``ClientList.vue``, which was removed 2026-07-13 -- this is now
        // the sole /clients surface.
        component: ClientsListPage,
        beforeEnter: requireCapability('client.read', '/distributor')
    },
    {
        // Retired: clients are created through "Invite a Client" on the list
        // (InviteClientModal). The old create form was a separate edit page.
        path: '/clients/new',
        redirect: { name: 'client_list' }
    },
    {
        // Phase 2.3 detail route: the WORKSPACE -- contact + Stores +
        // Approvals + People + Activity + Notes, edited inline (auto-save).
        path: '/clients/:id',
        meta: { deeplinkOwner: { type: 'client', param: 'id' } },
        name: 'client_detail',
        component: ClientDetailPage,
        props: true,
        beforeEnter: requireCapability('client.read', '/distributor')
    },
    {
        // Retired: the detail page is the single place to view AND edit a
        // client (inline auto-save). Kept as a redirect so bookmarks and
        // emailed links to the old edit page keep working.
        path: '/clients/:id/edit',
        redirect: (to) => ({ name: 'client_detail', params: { id: to.params.id } })
    }
]

const productRoutes = [
    {
        path: '/products',
        name: 'product_list',
        component: ProductList,
        // ACL-PR-13f: legacy was SA-only; product.view widens to SA + DM + SE + AB.
        // Matches the product_attribute / product_type pattern already swept in PR-8.
        beforeEnter: requireCapability('product.view', '/admin')
    },
    {
        path: '/products/add',
        name: 'product_add',
        component: ProductAddEdit,
        // ACL-PR-13f: legacy was SA-only; product.create widens to SA + DM + SE.
        beforeEnter: requireCapability('product.create', '/admin')
    },
    {
        path: '/products/:id',
        name: 'product_view',
        component: ProductView,
        // ACL-PR-13f: product.view (SA + DM + SE + AB).
        beforeEnter: requireCapability('product.view', '/admin')
    },
    {
        path: '/products/edit/:id',
        name: 'product_edit',
        component: ProductAddEdit,
        // ACL-PR-13f: legacy was SA-only; product.edit widens to SA + DM + SE.
        beforeEnter: requireCapability('product.edit', '/admin')
    },
    {
        // Canonical product editor (2026-06-13 design bundle, polish sprint
        // Day 6.5 graduation): the demo-sprint shell graduated to the
        // canonical route. Prior legacy ProductEditor + components/sections
        // chain deleted.
        //
        // Bookmarkable tab + placement URLs (2026-07-15, Jake -- "real paths,
        // NO #hash"): the editor is ONE page that swaps its ContextPanel
        // internally by selectedLeftRailItem. Rather than splitting it into
        // per-tab route COMPONENTS, the tab/placement is carried as a route
        // PARAM and synced to the store by useProductEditorRouteSync. Two
        // routes, most specific first:
        //   1. /placements/:placementId -> selects placement:<id>
        //   2. /:tab? (supplier-sku | sizes-colors | pricing | product-listing)
        //      -- the bare form (no :tab) redirects in-component to the
        //      last-viewed (else first) tab.
        // Both mount the SAME ProductEditor component; the optional-:tab route
        // must be registered AFTER the /placements/ route so "placements" is
        // never captured as a :tab slug.
        path: '/product-editor/:campaignProductId/placements/:placementId',
        meta: { deeplinkOwner: { type: 'campaign_product', param: 'campaignProductId' } },
        name: 'product_editor_placement',
        component: ProductEditor,
        beforeEnter: requireCapability('product.edit', '/admin')
    },
    {
        path: '/product-editor/:campaignProductId/:tab?',
        meta: { deeplinkOwner: { type: 'campaign_product', param: 'campaignProductId' } },
        name: 'product_editor',
        component: ProductEditor,
        beforeEnter: requireCapability('product.edit', '/admin')
    },
    // Legacy Subscription retirement PR 3a: the /admin_report route
    // (AdminReport.vue -> subscription.get_admin_report_data) was removed.
    // The report was built on the retired account_subscriptions/subscriptions
    // join; there is no per-account subscription data to report in the
    // per-distributor pricing model.
]

// Distributor-surface dashboard landing -- the Owner / Builder post-login
// home page. Moves from /admin to /distributor under PR-R2b.
//
// WHAT CHANGED AND WHY (card: distributor landing dashboard)
// ==========================================================
// This slot used to mount `CampaignList` -- the Stores list. That made
// `postLoginRedirect`'s capability branch decorative: it routes
// reporting.read holders to `/distributor` and everyone else to
// `/distributor/stores`, and its own comment says "Dashboard-capable users
// land on the dashboard", but BOTH arms rendered the same Stores list. The
// dashboard the code has been pointing at for months did not exist.
//
// Note the bare path was never a 404. Vue Router's default `strict: false`
// makes a trailing slash optional, so this record's concatenated
// `/distributor/` path matched `/distributor` fine -- it just rendered the
// wrong page. Both forms now reach the dashboard.
//
// THE GUARD, AND THE LOOP IT MUST NOT CREATE
// ==========================================
// The landing renders the dashboard-summary, which the backend gates on
// `distributor.reporting.read` (api/src/endpoints/dashboard.py). Sales Execs
// deliberately lack that cap (api/src/seeders/capability.py
// `_SALES_EXECUTIVE_CAPS`), so landing them here would 403 the page's only
// data source.
//
// The redirect target is `/distributor/stores`, NOT `/distributor`.
// `requireCapability(key, redirectTo)` calls `next(redirectTo)` on denial, so
// a self-referential target is an infinite navigation loop -- which is
// literally what the previous line here was (`requireCapability('campaign.edit',
// '/distributor')` sitting ON `/distributor`). It never fired only because
// every role able to reach this route already held `campaign.edit`. Sending
// denials to `/distributor/stores` terminates, because that route is gated on
// `distributor.stores.edit`, which team members DO hold -- and it is where
// `postLoginRedirect` already sends them.
const distributorHomePage = [
    {
        path: '/',
        name: 'distributor_landing',
        component: DistributorLanding,
        beforeEnter: requireCapability(
            'distributor.reporting.read',
            '/distributor/stores'
        )
    }
]

// SA-only platform admin pages (DevConsole, subscription admin, PR #1331
// Phase 3 mock pages) moved into router/groups/superadmin.js as part of the
// inline-arrays consolidation. See the header comment above for the full
// route inventory; routes are spread directly via `...superadminRoutes`
// in the aggregated routes table below.

// Owner/distributor help -- the markdown-backed in-app help surface
// (`docs/user/HELP-SYSTEM-DESIGN.md`). Replaces the legacy `/tutorials/*`
// and `/guides/*` routes + their hardcoded Vue components, which were
// DELETED (design decision 5) rather than redirected: they carried
// OrderWrite-era "campaign"/"pop-up" copy and video cards, ThreadFlow has
// never launched to production, and nobody has links to them.
//
// Mounted inside the `/distributor` group below, so it inherits the
// distributor auth guard and DistributorLayout chrome. `help.access` is
// kept as the capability gate -- the same admit set (SA/DM/SE/AB) that
// governed the legacy help routes, so no role gains or loses help access
// in this change.
//
// ROUTES DERIVE FROM FILES (design decision 3): `:slug` is a parameter
// resolved against `GET /api/help/owner/<slug>`, NOT a hand-maintained
// table of article paths. Adding `docs/user/owner/<name>.md` publishes a
// page with no route edit and no frontend rebuild.
const helpRoutes = [
    {
        path: '/help/owner',
        name: 'owner_help_index',
        component: OwnerHelpPage,
        beforeEnter: requireCapability('help.access', '/distributor')
    },
    {
        path: '/help/owner/:slug',
        name: 'owner_help_article',
        component: OwnerHelpPage,
        beforeEnter: requireCapability('help.access', '/distributor')
    }
]

const reportRoutes = [
    {
        path: '/report',
        name: 'report',
        component: Report,
        // route-cap-mapping.md (2026-05-15) flagged this route as an
        // open gap -- Report renders order/decoration metrics and was
        // unauthenticated. store.reporting (held by SA / DM / SE per the
        // seeder) is the correct gate: this is per-distributor operational
        // reporting, NOT the cross-distributor subscription admin_report
        // (that one is SA-only via superadmin.settings -- see admin_report
        // route above and GH #1864).
        beforeEnter: requireCapability('store.reporting', '/login')
    },
    // DistributorLayout sidebar links to /distributor/reporting/{orders,
    // performance, payouts}. Orders now binds the real cross-store orders
    // surface (DistributorOrdersList + per-order DistributorOrderDetail,
    // consuming GET /api/reporting/distributor-orders + /api/orders/:id --
    // both Owner/Builder scoped). Payouts still gets the DistributorComingSoon
    // frame. Performance binds PerformanceDashboard.vue (PR #1326).
    {
        path: '/reporting/orders',
        name: 'distributor_reporting_orders',
        component: DistributorOrdersList,
        beforeEnter: requireCapability('distributor.reporting.read', '/distributor')
    },
    {
        // Add New Order (card #566): staff order entry, moved here from the
        // deleted /admin-buyer/dashboard. Gated on order.create (Owner,
        // Builder, Sales Executive), NOT on distributor.reporting.read: the
        // page places an order, it reads no reporting. A static segment, so
        // it outranks the :orderId detail route below.
        path: '/reporting/orders/new',
        name: 'distributor_reporting_order_new',
        component: DistributorNewOrderPage,
        // Card #645: closed unless the SA switch `add_new_order_enabled` is
        // ON (default OFF); a direct visit while off lands on the Orders list.
        // Capability first, so a caller who cannot order never triggers the
        // settings read.
        beforeEnter: [
            requireCapability('order.create', '/distributor'),
            requireAddNewOrderEnabled({ name: 'distributor_reporting_orders' })
        ]
    },
    {
        // Per-order detail. props:true passes the :orderId route param straight
        // into the page (and on to OrderProfitBreakdown). Same capability as
        // the list so a distributor who can see the list can open a row.
        path: '/reporting/orders/:orderId',
        meta: { deeplinkOwner: { type: 'order', param: 'orderId' } },
        name: 'distributor_reporting_order_detail',
        component: DistributorOrderDetail,
        props: true,
        beforeEnter: requireCapability('distributor.reporting.read', '/distributor')
    },
    {
        path: '/reporting/performance',
        name: 'distributor_reporting_performance',
        component: PerformanceDashboard,
        beforeEnter: requireCapability('distributor.reporting.read', '/distributor')
    },
    {
        // Reports: pick a store, optionally narrow to one RELEASED batch
        // and a date range, choose a format, generate. Same capability as
        // its siblings so Builder accounts (which lack account.read) reach
        // it too -- the defect plan 5.1 fixed on Payouts.
        path: '/reporting/reports',
        name: 'distributor_reporting_reports',
        component: DistributorReportsPage,
        beforeEnter: requireCapability('distributor.reporting.read', '/distributor')
    },
    {
        path: '/reporting/payouts',
        name: 'distributor_reporting_payouts',
        // Plan 5.1: wired to real DistributorPayoutsPage; gate fixed from
        // distributor.account.read to distributor.reporting.read so Builder
        // accounts (which lack account.read) can also access this surface.
        component: DistributorPayoutsPage,
        beforeEnter: requireCapability('distributor.reporting.read', '/distributor')
    }
]
const publicRoutes = [
    {
        path: '/login',
        name: 'login_page',
        component: TheLoginPage
    },
    {
        // Email-change confirmation (card https://trello.com/c/BxskXBad).
        // PUBLIC by design: the link is delivered only to the NEW address
        // and is followed from that mailbox -- routinely a different
        // browser from the one holding the session, so requiring a login
        // would break the flow exactly when it is working correctly.
        // Authorization is possession of the single-use, expiring token,
        // the same model as the existing /password_reset/:token route
        // that sits in this same public group.
        path: '/confirm-email/:token',
        name: 'confirm_email_change',
        component: ConfirmEmailChangePage
    },
    {
        // The old /signup wizard was removed (Jake, 2026-10-20). The live
        // sign-up is /pricing -> /sign-up/:id. Marketing-site links and the
        // invite email still point at /signup, so it redirects (kept public
        // in requireToken.js so a logged-out visitor is not bounced to login
        // before the redirect runs).
        path: '/signup',
        redirect: { name: 'pricing_page' }
    },
    {
        path: '/pricing',
        name: 'pricing_page',
        component: PricingPage
    },
    {
        // The Master Services Agreement (the ONE agreement; backend
        // AGREEMENT_URL default) with the Owner's confirm at its foot.
        // PUBLIC (listed in requireToken.js): anyone may read it; the
        // confirm asks the server who is signed in.
        path: '/legal/agreement',
        name: 'legal_agreement',
        component: AgreementPage
    },
    {
        path: '/',
        name: 'dashboard',
        component: CampaignList,
        // T3.1 (PR-3 fix-loop, 2026-05-14): tightened from `campaign.view`
        // (held by Shopper via `_UNIVERSAL_VIEW_CAPS`) to `campaign.edit`
        // (legacy admit set: DM + SA + SE + AB).
        //
        // The initial T3.1 commit (df4a95b2a) only tightened the
        // `home_page` route under `/admin/`. The bare `/` `dashboard`
        // route was missed — Vue Router matches bare `/` to this entry
        // because publicRoutes is registered before /admin routes.
        // Shopper hitting `/` (bookmark, direct navigation, etc.) would
        // still mount admin `CampaignList`. Bar-raiser P0 caught this
        // in PR-3 /gate.
        //
        // Backend row-scoping (`_campaign_perms`) was already filtering
        // the data, so this is a UX/route-defense-in-depth fix, not a
        // PII leak — but it closes the bug T3.1 was advertised to fix.
        beforeEnter: requireCapability('campaign.edit', '/admin')
    },
    /* add sales repo or verify distributor */
    // Storefront routes (/shop, /store/:id, /store/:slug/*) moved to
    // router/groups/storefront.js (single source). Spread below as
    // `...storefrontRoutes` in the aggregated routes table. Fix:
    // wire-storefront-routes (issue #1) -- the group was previously
    // duplicated inline here AND the new Phase 2-A/B gallery + detail
    // routes were missing inline, so they 404'd.
    {
        path: '/password_reset/:id',
        name: 'reset_password_page',
        component: TheResetPasswordPage
    },
    {
        // Phase F5 / ADR 0050: Client Store Owner invitation magic-link
        // landing. The recipient is pre-auth; the token IS the credential.
        // The page calls the backend's check + accept endpoints
        // (/api/auth/accept-invitation/:token) and on success persists
        // the bearer + routes to /portal/welcome.
        path: '/auth/accept-invitation/:token',
        name: 'accept_invitation',
        component: AcceptInvitation,
    },
    /* add sales repo or verify distributor */
    {
        path: '/new-user/:id',
        name: 'add_credentials',
        component: AddCredentials
    },
    {
        path: '/sign-up/:id',
        name: 'sign-up',
        component: SignUp
    }
]

// PR-R5: convert the REDIRECTS map (router/redirects.js) into Vue Router
// `{ path, redirect }` route definitions. Function destinations are
// passed through verbatim (Vue Router supports them natively); string
// destinations also work as-is. These carry the legacy flat storefront
// paths (/cart, /orders, /account/...) to the nested /store/:slug/* form.
const redirectRoutes = Object.entries(REDIRECTS).map(([path, redirect]) => ({
    path,
    redirect,
}))

// --- Route-fork PR-R2b: redirect legacy /admin/* Owner/Builder URLs to
// their canonical /distributor/* destinations. SA-only routes (accounts,
// distributors admin, developer_console, subscriptions) stay at /admin/*.
//
// These are exhaustive enough that bookmarks, email links, and stale
// router-link targets all resolve to the new surface.
const legacyAdminRedirects = [
    // NOTE: the former `{ path:'/admin', redirect:'/distributor' }` bounce was
    // removed -- bare `/admin` now resolves to the SA Dashboard
    // (sa_dashboard route in superadminRoutes, registered above this block so
    // it wins). Non-SA users hitting `/admin` are redirected to /distributor
    // by sa_dashboard's requireCapability guard. The deep /admin/* sub-paths
    // below remain pure Owner-URL compatibility redirects (no SA equivalent
    // exists at those paths).
    { path: '/admin/campaigns', redirect: '/distributor/stores' },
    { path: '/admin/campaigns/add', redirect: '/distributor/stores/add' },
    // The admin campaign editor moved onto the nested store-editor surface.
    // Bare `/admin/campaigns/:id` lands on the editor; the `/edit/:tab`
    // variant translates the legacy tab name to its new consolidated
    // section via OLD_TAB_TO_SECTION (unknown tabs clamp to the landing).
    // hasOwnProperty.call guards against prototype-chain hits like
    // `?tab=toString` returning a truthy function reference.
    {
        path: '/admin/campaigns/:id/edit/:tab',
        redirect: to => {
            const tab = to.params.tab;
            const section = Object.prototype.hasOwnProperty.call(
                OLD_TAB_TO_SECTION,
                tab
            )
                ? OLD_TAB_TO_SECTION[tab]
                : null;
            return section
                ? `/distributor/stores/${to.params.id}/edit/${section}`
                : `/distributor/stores/${to.params.id}/edit`;
        }
    },
    { path: '/admin/campaigns/:id', redirect: to => `/distributor/stores/${to.params.id}/edit` },
    // Vendor catalog redirects removed 2026-08-28 with the vendor UI
    // itself: `/distributor/vendors*` pointed at `/admin/vendors*`,
    // which no longer exists, so keeping them would have redirected
    // stale bookmarks into the catch-all NotFound instead of 404ing
    // honestly. Their absence is asserted in
    // `router/__tests__/legacy-vendor-devconsole-routes-removed.spec.js`.
    { path: '/admin/ccpayments', redirect: '/distributor/account/payments' },
    { path: '/admin/setup', redirect: '/distributor/setup' },
    // `/admin/profile` used to be a pure Owner-URL compatibility redirect
    // to the distributor COMPANY profile. As of 2026-08-11 (card
    // https://trello.com/c/BxskXBad) `/admin/profile` is a REAL SA route
    // mounting the personal Profile page, registered in
    // `superadminRoutes` above this block -- so it wins and this entry no
    // longer fires for it. The legacy COMPANY destination is preserved at
    // its own unambiguous path below, so an old Owner bookmark still
    // reaches the company page rather than silently landing on a
    // different page that inherited the URL.
    { path: '/admin/company', redirect: '/distributor/account/company' },
    { path: '/admin/account/profile', redirect: '/distributor/account/company' },
    { path: '/admin/report', redirect: '/distributor/report' },
    // Legacy Subscription retirement PR 3a: /distributor/admin_report was
    // removed (SA subscription report on the retired tables), so the legacy
    // /admin/admin_report -> /distributor/admin_report redirect was dropped.
    { path: '/admin/products', redirect: '/distributor/products' },
    { path: '/admin/products/add', redirect: '/distributor/products/add' },
    { path: '/admin/products/edit/:id', redirect: to => `/distributor/products/edit/${to.params.id}` },
    { path: '/admin/products/:id', redirect: to => `/distributor/products/${to.params.id}` },
    { path: '/admin/product-editor/:campaignProductId', redirect: to => `/distributor/product-editor/${to.params.campaignProductId}` },
    // `/admin/tutorials*` and `/admin/guides*` redirects were REMOVED
    // 2026-08-18: they pointed at `/distributor/tutorials|guides`, which no
    // longer exist (`docs/user/HELP-SYSTEM-DESIGN.md` decision 5 deletes the
    // legacy help surfaces outright rather than redirecting them). Keeping
    // them would have produced a redirect into a 404 -- worse than a
    // straight 404, because the user lands on an unrelated URL first.
]

const router = createRouter({
    history: createWebHistory(),
    routes: [
        ...withPrefix('', publicRoutes),

        // Shopper-facing routes (/shop, /store/:id, /store/:slug/*). Spread
        // BEFORE redirectRoutes so the canonical paths register first; the
        // /shop/:id legacy redirect in REDIRECTS still fires for the
        // /shop/<slug> legacy URL (path doesn't overlap with /shop bare).
        // The group carries absolute paths + per-route meta.layout
        // ('storefront'), so it spreads directly -- NOT through withPrefix.
        ...storefrontRoutes,

        ...redirectRoutes,

        // #732 made bare `/profile` redirect to `/distributor/profile`,
        // which at the time was the distributor COMPANY profile.
        //
        // 2026-08-11 (card https://trello.com/c/BxskXBad): that target now
        // mounts the PERSONAL profile, so this redirect is repointed at
        // `/distributor/account/company` -- the company page's new
        // canonical home. Anyone who bookmarked bare `/profile` bookmarked
        // the company page, so the company page is where they must still
        // land; silently delivering them to a different page that happens
        // to share the old URL would be the worse outcome.
        { path: '/profile', redirect: '/distributor/account/company' },

        // The Payments page moved under /distributor, but Stripe had already
        // been told to return distributors to the ROOT path: the Connect
        // onboarding AccountLink used /account/payments?stripe_return=1 until
        // 2026-09-29 and rendered NotFound on prod. This catches links Stripe
        // already issued, bookmarks, and any stale redirect setting. A string
        // redirect carries the query over, which is load-bearing: PaymentsPage
        // reads stripe_return / code / state from it. Only this one path is
        // redirected -- the root /account/* namespace also holds SHOPPER
        // legacy redirects (router/redirects.js), so siblings are not guessed.
        { path: '/account/payments', redirect: '/distributor/account/payments' },

        // Owner / Builder / SE app surface -- post route-fork PR-R2b.
        // sharedMeta: { layout: 'distributor' } picks DistributorLayout
        // in App.vue's currentLayout computed -- the shell that includes
        // the left sidebar, topbar breadcrumb, and ImpersonationBanner.
        // Previously these routes had no meta.layout and fell through
        // to PublicLayout (no chrome) per the App.vue fallback.
        ...withPrefix('/distributor',
            [
                ...distributorProfileRoutes,
                ...distributorAccountRoutes,
                ...storeRoutes,
                // There is no vendor-catalog route here: the 2026-06-08
                // decision moved it to SA-only, and 2026-08-28 deleted
                // that surface outright as legacy OrderWrite. Per the
                // CLAUDE.md routing model distributors pick SUPPLIERS,
                // and the modern surface is the SA-owned
                // `/admin/suppliers/non-integrated`.
                ...orderRoutes,
                ...onboardingRoutes,
                ...reportRoutes,
                ...productRoutes,
                ...decorationLibraryRoutes,
                ...clientsRoutes,
                ...distributorHomePage,
                ...helpRoutes
            ],
            { layout: 'distributor' }
        ),

        // SA-only `/admin/*` surface. The group carries absolute paths +
        // per-route guards + meta.layout:'superadmin', so it is spread
        // directly (NOT via withPrefix) -- same pattern as portalRoutes.
        // router/groups/superadmin.js is the single source for these routes
        // (was four inline arrays here that lacked meta.layout and omitted
        // /admin/operations + /admin/risk).
        ...superadminRoutes,

        // Legacy /admin/* Owner/Builder URL compatibility -- redirect to new
        // /distributor/* surface. These come AFTER the /admin/* route block
        // so SA-only routes resolve first; only paths that don't match an
        // SA route fall through to the redirect map.
        ...legacyAdminRedirects,

        // Route-fork PR-R4: Client Portal surface. Routes carry absolute
        // paths and per-route requireCapability guards already (see
        // `router/groups/portal.js`), so they are spread in directly --
        // NOT wrapped in withPrefix. Phase 1 placeholders only; Phase 4
        // swaps the component bodies.
        ...portalRoutes,

        { path: '/:pathMatch(.*)*', component: TheNotFoundPage }
    ]
})

// check for user authentication before rendering the requested page
router.beforeEach(requireToken)

// Keep `?preview=1` attached for the whole store-editor preview session.
// The preview iframe is interactive, and in-storefront navigation targets
// are bare path strings that would otherwise DROP the flag on the first
// click -- landing the same-origin iframe on a normal shopper route whose
// token swap would clobber the editor's (impersonation) session.
// See router/storefrontPreview.js.
installPreviewQueryPersistence(router)

// Keep `?framed=1` attached while the reader browses INSIDE the help
// flyover's iframe. Same class of problem as the preview flag above:
// HelpRenderer's nav and breadcrumb are bare path strings, so the first
// in-article click would drop the flag and the article would lose the
// sidebar + breadcrumb it needs to keep being browsable. (The flag no
// longer selects the app layout -- help routes render with minimal chrome
// in both presentations, see App.vue's currentLayout -- but it still
// governs that in-article navigation.)
//
// Registered AFTER requireToken deliberately. This guard only ever adds a
// query param to a help route that was already reachable; running it after
// the auth guard means it can never rewrite a navigation that requireToken
// was about to redirect, so the flag cannot interact with the auth
// boundary at all. See router/helpFramed.js.
installHelpFramedQueryPersistence(router)

// Store-wide password wall.
//
// Registered AFTER installPreviewQueryPersistence so that a preview
// navigation has already had `?preview=1` re-attached by the time this guard
// asks "is this a preview?". Without that ordering the first in-preview click
// would arrive here flagless and the editor would be walled out of previewing
// its own password-protected store.
//
// Registered after requireToken for the same reason in reverse: requireToken
// establishes the session signals (roleName / impersonation breadcrumb) that
// isPrivilegedSession() reads to let a back-office user through.
router.beforeEach(
    createStorefrontPasswordGate(createAxiosLookup(axiosInstance))
)

// Dismiss the global notification toast when the user moves to a different
// page. Errors deliberately persist (timeout -1) so they can be read and acted
// on, but that persistence is scoped to the page that raised them -- an error
// about a form the user has already left is noise sitting over the next page's
// UI. Registered LAST so it observes the final, committed destination of any
// redirect the guards above perform, and it runs as an afterEach so a
// cancelled navigation never costs the user a toast.
//
// Deliberately does NOT clear a toast raised by the very navigation it is
// reacting to: several pages raise an error and then push (see the module
// header), and eating those would hide the reason the user was bounced.
installNotificationDismissOnRouteChange(router, store)

// Browser tab: document title, favicon and the og pair, on storefront routes
// only (white-label Unit 6). index.html is static and GLOBAL -- one file shared
// with admin, distributor and superadmin -- so a per-store value can only be
// injected at runtime; its tags stay put and become the defaults this hook
// restores to.
//
// Registered LAST, for the same reason the notification dismissal above is: it
// must observe the final, committed destination of any redirect a guard
// performs. Branding the tab for a route the visitor was bounced out of would
// leave a store's identity on a page showing something else.
installStorefrontChromeWatch(
    store,
    installStorefrontBrowserChrome(router, store)
)

// The storefront resume code rides in the URL fragment (`#r=<code>`); every
// in-app navigation builds a fragment-less URL, so it is re-applied after
// each one (router/storefrontResumeCode.js). An afterEach, like the two above,
// so it writes onto the final committed URL.
installResumeFragment(router)

export default router
